import type { LocationRewrite } from "@tanstack/react-router";
import { config } from "./config";

/**
 * The platform admin console lives on its own host (admin.guv.dev) behind Cloudflare Access. There the router still
 * sees `/admin/...` while the address bar shows `/...`; on every other host `/admin` sends you over. With
 * VITE_ADMIN_URL unset (local development, mocks) the console stays at `/admin` on the same origin.
 */
const adminOrigin = config.adminUrl ? new URL(config.adminUrl).origin : "";

export const onAdminHost = adminOrigin !== "" && typeof window !== "undefined" && window.location.origin === adminOrigin;

/** True on the app host when the console lives elsewhere: links to it must leave the page. */
export const adminElsewhere = adminOrigin !== "" && !onAdminHost;

const isAdminPath = (p: string) => p === "/admin" || p.startsWith("/admin/");
// Pages that keep their own meaning on the admin host: signing in, and approving a Tier 3 change.
const sharedPath = (p: string) => p === "/login" || p.startsWith("/approve/");

export const adminRewrite: LocationRewrite = {
  input: ({ url }) => {
    if (isAdminPath(url.pathname) || sharedPath(url.pathname)) return undefined;
    const u = new URL(url.href);
    u.pathname = url.pathname === "/" ? "/admin" : `/admin${url.pathname}`;
    return u;
  },
  output: ({ url }) => {
    if (!isAdminPath(url.pathname)) return undefined;
    const u = new URL(url.href);
    u.pathname = url.pathname.slice("/admin".length) || "/";
    return u;
  },
};

/** Full URL of a console page (`/admin/...`, as the router knows it) on the admin host. */
export function adminUrl(path: string): string {
  return `${adminOrigin}${path.replace(/^\/admin(?=\/|$|\?)/, "") || "/"}`;
}

/** Full URL of a dashboard page on the app host, for links out of the console. */
export function appUrl(path: string): string {
  return `${config.publicUrl.replace(/\/$/, "")}${path}`;
}
